import { PastureMapPanel } from "../PastureMapPanel";
import { managementTypeOption, managementGroupOption } from "../managementOptions";
import { useReportEffect } from "../../../../hooks/useReportEffect";
import { ReportTop } from "../../../../components/Dashboard/ReportLayout";
import { FilterDateDashboard } from "../../../../components/Dashboard/ViewDatas/FilterDate";
import { ReactComponent as BagFood } from "../../../../assets/svg/icons/bagFood.svg";
import { DateModal } from "../../../../components/Modal/DateFilter";
import { useFilter } from "../../../../hooks/useFilter";
import Constants from "../../../../constants/Index";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MultiDropdownV2, OptionV2 } from "../../../../components/FormControlsV2";
import { Controller, useForm } from "react-hook-form";
import { format } from "date-fns";
import ChartBar from "../../../../components/Graph/Bar";
import ChartMultiLine from "../../../../components/Graph/MultiLine";
import { IOptionDoubleValuesProps, IOptionProps, IOptionsProps } from "../../../../@types/reactSelect/IOption";
import { exportPastureManagementMicroAreaDataToExcel } from "../../../../utils/excel/exportPastureManagementMicroAreaData";
import { flattenSelection, useFilterContextV2, selectionToNumericIds, selectionToSortedIdsKey } from "../../../../contexts/FilterContextV2";

export default function DashboardManagementMicroArea() {
    const { t } = useTranslation();
    const {
        rangeDays,
        setRangeDays,
        setHideMicroAreaFilter,
        setHideBatchFilter,
        setDashboard,
        setSideBarOptionSelected,
        setSelectRangeDate,
        snapshotMicroAreaMetrics,
        setSnapshotMicroAreaMetrics,
    } = useFilter();

    const selection = useFilterContextV2();
    const selectedFarms = useMemo(() => flattenSelection(selection.selectedFarms), [selection.selectedFarms]);
    const selectedAreas = useMemo(() => flattenSelection(selection.selectedAreas).filter(area =>
        selectedFarms.some(farm => String(farm.value) === String(area.info?.farm?.id))), [selection.selectedAreas, selectedFarms]);
    const [focusedFarmId, setFocusedFarmId] = useState<string | null>(null);

    const farmsKey = useMemo(
        () => selectionToSortedIdsKey(selectedFarms),
        [selectedFarms],
    );
    const areasKey = useMemo(
        () => selectionToSortedIdsKey(selectedAreas),
        [selectedAreas],
    );

    const {
        handleSubmit,
        watch,
        control,
        setValue
    } = useForm();

    const [optionsTypeManagement, setOptionsTypeManagement] = useState<IOptionsProps>([]);
    const [lotInstConsumo, setLotInstConsumo] = useState<IOptionDoubleValuesProps>();
    const [lotInstConsumoMicroArea, setLotInstConsumoMicroArea] = useState<IOptionsProps>([]);
    const [lotInstAltura, setLotInstAltura] = useState<IOptionDoubleValuesProps>();
    const [lotInstAlturaMicroArea, setLotInstAlturaMicroArea] = useState<IOptionsProps>([]);
    const [lotInstNota, setLotInstNota] = useState<IOptionDoubleValuesProps>();
    const [lotInstNotaMicroArea, setLotInstNotaMicroArea] = useState<IOptionsProps>([]);
    const [lotInstMovFaixa, setLotInstFaixa] = useState<IOptionDoubleValuesProps>();
    const [lotInstMovFaixaMicroArea, setLotInstFaixaMicroArea] = useState<IOptionsProps>([]);
    const [managementOptions, setManagementOptions] = useState<IOptionsProps>();
    const [lotInst, setLotInst] = useState<IOptionsProps>([]);
    const [microAreas, setMicroAreas] = useState<IMicroArea[]>();
    const [microAreasSelected, setMicroAreasSelected] = useState<number[]>([]);

    useEffect(() => { setValue("managementGroup", []); }, [farmsKey, areasKey, setValue]);

    const typeManagement = watch("managementType")
    const groupManagement = watch("managementGroup")
    useEffect(() => { setValue("managementGroup", []); }, [typeManagement, setValue]);

    useEffect(() => {
        setHideMicroAreaFilter(true)
        setHideBatchFilter(true)
        setSideBarOptionSelected("DASHBOARD")
        setDashboard("MANAGEMENT_PASTURE")
        setSelectRangeDate(0)
        document.title = Constants.TITLE_DASHBOARD;
        const now = new Date();
        const firstDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        setRangeDays({
            from: firstDayOfLastMonth,
            to: firstDayOfCurrentMonth
        })
    }, []);

  useReportEffect((axios) => {
    axios
      .get("/micro-area-management/types")
      .then((response) => {
        const types: IOptionsProps = response.data.map((type: any) => ({
          ...managementTypeOption(type),
          value: String(type.id),
        }))
        setOptionsTypeManagement(types.filter((type: any) => type.value != "1"))
      }).catch(() => undefined)
    axios.get("/micro-areas").then(response => setMicroAreas(response.data)).catch(() => undefined)
  }, []);

  useReportEffect((axios) => {
    setManagementOptions([]);
    const farmIds = selectionToNumericIds(selectedFarms);
    const areaIds = selectionToNumericIds(selectedAreas);
    if (!rangeDays || !farmIds.length || !areaIds.length) return;
    const params = new URLSearchParams({
      startDate: format(rangeDays.from || new Date(), 'yyyy-MM-dd'),
      endDate: format(rangeDays.to || new Date(), 'yyyy-MM-dd'),
    });
    farmIds.forEach(id => params.append("farmIds", String(id)));
    areaIds.forEach(id => params.append("areaIds", String(id)));
    axios.get<IAnalyticsManagementGroupsByFarm[]>(`/analytics/snapshots/management-groups?${params}`)
      .then(({ data }) => setManagementOptions(data.map(group => {
        const option = managementGroupOption(group);
        return {
          ...option,
          value: `${group.farm.id}:${option.value}`,
          label: `${group.farm.name} · ${option.label}`,
          info: { ...group, farmId: String(group.farm.id), farmName: group.farm.name },
        };
      }))).catch(() => undefined);
  }, [rangeDays, farmsKey, areasKey]);

  useReportEffect((axios) => {
    setSnapshotMicroAreaMetrics(undefined as any);
    if (!groupManagement?.length || !selectedAreas.length) return;
    if (rangeDays) {
      let startDate = format(rangeDays?.from ? new Date(rangeDays?.from) : new Date(), 'yyyy-MM-dd')
      let endDate = format(rangeDays?.to ? new Date(rangeDays?.to) : new Date(), 'yyyy-MM-dd')

      if (groupManagement) {
        const microAreas = groupManagement.map((management: IOptionProps) => (management.info as IAnalyticsManagementGroups).micro_areas.map(microArea => microArea.id)).flat()
        if (microAreas.length > 0)
          axios.get(`/analytics/snapshots/micro-areas/metrics?startDate=${startDate}&endDate=${endDate}&${microAreas.length > 0 ? microAreas.map((microArea: number) => `microAreaIds=${microArea}`).join("&") : ""}`)
            .then((response) => {
              setSnapshotMicroAreaMetrics(response.data)
            }).catch(() => undefined)
      }
    }
  }, [rangeDays, groupManagement, farmsKey, areasKey]);

    const buildMicroAreaLabel = (microArea: any) => {
        const parts = [
            microArea?.farm_initials,
            microArea?.area_initials,
            microArea?.initials
        ].filter(Boolean);

        return parts.join("-");
    }

    useEffect(() => {
        if (!snapshotMicroAreaMetrics) {
            setLotInstConsumo(undefined); setLotInstConsumoMicroArea([]);
            setLotInstAltura(undefined); setLotInstAlturaMicroArea([]);
            setLotInstNota(undefined); setLotInstNotaMicroArea([]);
            setLotInstFaixa(undefined); setLotInstFaixaMicroArea([]);
            setLotInst([]);
            return;
        }
        if (snapshotMicroAreaMetrics) {
            setLotInstConsumo({
                data1: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.total_consumption_kg
                })),
                data2: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_instant_capacity_rate
                }))
            })
            setLotInstConsumoMicroArea(snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                label: inst.date,
                value: inst.total_consumption_kg,
                info: inst.micro_area.map((microArea) => ({
                    initials: buildMicroAreaLabel(microArea),
                    value: microArea.consumption_kg || 0
                })).sort((a, b) => a.initials.localeCompare(b.initials))
            })))
            setLotInstAltura({
                data1: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_height
                })),
                data2: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_instant_capacity_rate
                }))
            })
            setLotInstAlturaMicroArea(snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                label: inst.date,
                value: inst.average_height,
                info: inst.micro_area.map((microArea) => ({
                    initials: buildMicroAreaLabel(microArea),
                    value: microArea.height || 0
                })).sort((a, b) => a.initials.localeCompare(b.initials))
            })))

            setLotInstNota({
                data1: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_rating
                })),
                data2: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_instant_capacity_rate
                }))
            })
            setLotInstNotaMicroArea(snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                label: inst.date,
                value: inst.average_rating,
                info: inst.micro_area.map((microArea) => ({
                    initials: buildMicroAreaLabel(microArea),
                    value: microArea.rating || 0
                })).sort((a, b) => a.initials.localeCompare(b.initials))
            })))

            setLotInst(snapshotMicroAreaMetrics.daily_metrics.map((inst) => ({
                label: inst.date,
                value: inst.average_instant_capacity_rate,
                info: inst.micro_area.map((microArea) => ({
                    initials: buildMicroAreaLabel(microArea),
                    value: microArea.average_capacity_rate || 0
                })).sort((a, b) => a.initials.localeCompare(b.initials))
            })))

            setLotInstFaixa(({
                data1: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.strip_movements_count
                })),
                data2: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_instant_capacity_rate
                }))
            }))

            setLotInstFaixaMicroArea(snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                label: inst.date,
                value: inst.strip_movements_count,
                info: inst.micro_area.map((microArea) => ({
                    initials: buildMicroAreaLabel(microArea),
                    value: microArea.strip_movements || 0
                })).sort((a, b) => a.initials.localeCompare(b.initials))
            })))
        }
    }, [snapshotMicroAreaMetrics]);

    useEffect(() => {
        setValue("managementGroup", undefined);
        setLotInstConsumo(undefined);
        setLotInstConsumoMicroArea([]);
        setLotInstAltura(undefined);
        setLotInstAlturaMicroArea([]);
        setLotInstNota(undefined);
        setLotInstNotaMicroArea([]);
        setLotInstFaixa(undefined);
        setLotInstFaixaMicroArea([]);
        setLotInst([]);
        setSnapshotMicroAreaMetrics(undefined as any);
    }, [farmsKey]);

    const renderManagementGroupOption = (option: OptionV2) => {
        const infoOption: IAnalyticsManagementGroups = option.info;

        return (
            <div className="pasture-group-option">
                {option.label}
                <br />
                {infoOption.micro_areas.length > 0 ? infoOption.micro_areas.map(microArea => microArea.initials).join(" ") : ""}
            </div>
        );
    }

    const focusManagementGroup = (option: OptionV2) => setFocusedFarmId(option.info.farmId);

    useEffect(() => {
        setMicroAreasSelected((groupManagement || []).flatMap((management: IOptionProps) => (management.info as IAnalyticsManagementGroups).micro_areas.map(microArea => microArea.id)))
    }, [groupManagement]);

    return (
        <div className="cattleMap_background" style={{ height: "100vh", overflowY: "auto" }}>
            <DateModal />

            <form onSubmit={() => handleSubmit(() => { })} className="dashboardPastureManagement dashboardPastureManagement--multi-farm">
                <div className="cattleMap_page">
            <ReportTop kind="management" multipleManagementFarms icon={<BagFood height={72} />} title={t("navigation.pastureManagement")} subtitle={t("reports.paddockView")}>
              <div>
                <div className="pasture-period-filter">
                <FilterDateDashboard
                  onExportExcel={() => {
                    if (snapshotMicroAreaMetrics) {
                      try {
                        exportPastureManagementMicroAreaDataToExcel(
                          snapshotMicroAreaMetrics,
                          rangeDays?.from ? new Date(rangeDays.from) : undefined,
                          rangeDays?.to ? new Date(rangeDays.to) : undefined
                        );
                      } catch (error) {
                        console.error('Erro ao exportar:', error);
                        alert(t("reports.exportError"));
                      }
                    }
                  }}
                />
                </div>
                <br />
                <div className="management-filters-layout">
                  <div className="management-filters-fields">
                  <Controller
                    name="managementType"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <MultiDropdownV2
                        name="managementType"
                        label={t("reports.grazingType")}
                        placeholder={t("reports.select")}
                        menuMount="static"
                        emptyMessage={t("reports.noOptions")}
                        options={optionsTypeManagement}
                        value={field.value ?? []}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={error?.message}
                      />
                    )}
                  />

                  <Controller
                    name="managementGroup"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <MultiDropdownV2
                        name="managementGroup"
                        label={t("reports.managementGroup")}
                        placeholder={t("reports.select")}
                        menuMount="static"
                        emptyMessage={t("reports.noOptions")}
                        options={typeManagement ? (managementOptions?.filter(option => Array.from(typeManagement.map(((type: IOptionProps) => type.value))).includes(`${option.info.management_type.id}`)) || []) : []}
                        value={field.value ?? []}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={error?.message}
                        renderOption={renderManagementGroupOption}
                        onOptionFocus={focusManagementGroup}
                        searchable
                        selectAll
                      />
                    )}
                  />

                  </div>

                  <PastureMapPanel
                    selectedFarms={selectedFarms}
                    focusedFarmId={focusedFarmId}
                    setFocusedFarmId={setFocusedFarmId}
                    microAreas={microAreas}
                    microAreasSelected={microAreasSelected}
                  />
                </div>
              </div>
            </ReportTop>
                </div>

                <br />

                <div className="content">
                    {/* <ChartBarLine
                        titleGraph={"Taxa de lotação instantânea e kg de suplemento fornecido por dia"}
                        dataColumn={lotInstConsumo?.data1 || []}
                        dataLine={[]}
                        colorIcons={["Kg de suplemento fornecido", "Média de Tx lotação instantânea"]}
                        axisLabels={["", "Kg de suplemento", "Tx lotação instantânea"]}
                        fullWidth
                        info={lotInstConsumoMicroArea.map(inst => inst.info)}
                    /> */}
                    <ChartBar
                        titleGraph={t("reports.suppliedSupplementKg")}
                        data={lotInstConsumo?.data1}
                        axisLabels={["", t("reports.suppliedSupplementAxis")]}
                        fullWidth
                        info={lotInstConsumoMicroArea?.map(inst => inst.info)}
                        monoColor
                    />
                    {typeManagement && typeManagement.map(((type: IOptionProps) => type.value)).includes("2") ? <>
                        <div className="noBreakComponent">
                            <div className="infoContentGraph">
                                <h2>{t("reports.intervalDays", { count: snapshotMicroAreaMetrics?.daily_metrics.length })}</h2>
                                <h2>{t("reports.heightCollections", { count: snapshotMicroAreaMetrics?.total_monitoring_counts.height_count })}</h2>
                                <h2>{t("reports.averageOccupationDays", { value: Number(snapshotMicroAreaMetrics?.total_occupation_days.average_occupied_days).toFixed(1) })}</h2>
                                <h2>{t("reports.averageRestDays", { value: Number(snapshotMicroAreaMetrics?.total_occupation_days.average_rest_days).toFixed(1) })}</h2>
                            </div>
                            {/* <ChartBarLine
                                titleGraph={"Taxa de lotação instatânea e altura de pasto em manejo contínuo"}
                                dataColumn={lotInstAltura?.data1 || []}
                                dataLine={lotInstAltura?.data2 || []}
                                colorIcons={["Média de Monitoramento", "Média de Tx lotação instantânea"]}
                                axisLabels={["", "Altura", "Tx lotação instantânea"]}
                                fullWidth
                                info={lotInstAlturaMicroArea.map(inst => inst.info)}
                            /> */}
                            <ChartBar
                                titleGraph={t("reports.continuousHeight")}
                                data={lotInstAltura?.data1}
                                axisLabels={["", t("reports.averageHeight")]}
                                fullWidth
                                info={lotInstAlturaMicroArea?.map(inst => inst.info)}
                                monoColor
                            />
                        </div>
                        <div className="noBreakComponent">
                            <div className="infoContentGraph">
                                <h2>{t("reports.intervalDays", { count: snapshotMicroAreaMetrics?.daily_metrics.length })}</h2>
                                <h2>{t("reports.ratingCollections", { count: snapshotMicroAreaMetrics?.total_monitoring_counts.rating_count })}</h2>
                                <h2>{t("reports.averageOccupationDays", { value: Number(snapshotMicroAreaMetrics?.total_occupation_days.average_occupied_days).toFixed(1) })}</h2>
                                <h2>{t("reports.averageRestDays", { value: Number(snapshotMicroAreaMetrics?.total_occupation_days.average_rest_days).toFixed(1) })}</h2>
                            </div>

                            {/* <ChartBarLine
                                titleGraph={"Taxa de lotação instatânea e nota de pasto em manejo contínuo"}
                                dataColumn={lotInstNota?.data1 || []}
                                dataLine={lotInstNota?.data2 || []}
                                colorIcons={["Média de Monitoramento", "Média de Tx lotação instantânea"]}
                                axisLabels={["", "Nota", "Tx lotação instantânea"]}
                                fullWidth
                                info={lotInstNotaMicroArea.map(inst => inst.info)}
                            /> */}
                            <ChartBar
                                titleGraph={t("reports.continuousRating")}
                                data={lotInstNota?.data1}
                                axisLabels={["", t("reports.averageRating")]}
                                fullWidth
                                info={lotInstNotaMicroArea?.map(inst => inst.info)}
                                monoColor
                            />
                        </div>
                    </> : <></>
                    }
                    {typeManagement && typeManagement.map(((type: IOptionProps) => type.value)).includes("3") ? <>
                        <div className="noBreakComponent">
                            <div className="infoContentGraph">
                                <h2>{t("reports.averageOccupationDays", { value: Number(snapshotMicroAreaMetrics?.total_occupation_days.average_occupied_days).toFixed(1) })}</h2>
                                <h2>{t("reports.averageRestDays", { value: Number(snapshotMicroAreaMetrics?.total_occupation_days.average_rest_days).toFixed(1) })}</h2>
                            </div>
                            <ChartMultiLine
                                titleGraph={t("reports.rotationalStocking")}
                                data={lotInst}
                                axisLabels={[t("reports.day"), t("reports.stockingRate")]}
                                fullWidth
                            />
                        </div>
                        {snapshotMicroAreaMetrics?.total_monitoring_counts.height_count != 0 &&
                            <div className="noBreakComponent">
                                <div className="infoContentGraph">
                                    <h2>{t("reports.intervalDays", { count: snapshotMicroAreaMetrics?.daily_metrics.length })}</h2>
                                    <h2>{t("reports.heightCollections", { count: snapshotMicroAreaMetrics?.total_monitoring_counts.height_count })}</h2>
                                    <h2>{t("reports.averageOccupationDays", { value: Number(snapshotMicroAreaMetrics?.total_occupation_days.average_occupied_days).toFixed(1) })}</h2>
                                    <h2>{t("reports.averageRestDays", { value: Number(snapshotMicroAreaMetrics?.total_occupation_days.average_rest_days).toFixed(1) })}</h2>
                                </div>
                                {/* <ChartBarLine
                                    titleGraph={"Taxa de lotação instatânea e altura de pasto em manejo rotacionado"}
                                    dataColumn={lotInstAltura?.data1 || []}
                                    dataLine={lotInstAltura?.data2 || []}
                                    colorIcons={["Média de Monitoramento", "Média de Tx lotação instantânea"]}
                                    axisLabels={["", "Altura", "Tx lotação instantânea"]}
                                    fullWidth
                                    info={lotInstAlturaMicroArea.map(inst => inst.info)}
                                /> */}
                                <ChartMultiLine
                                    titleGraph={t("reports.rotationalHeight")}
                                    data={lotInstAlturaMicroArea}
                                    axisLabels={[t("reports.day"), t("pastureMap.heightAxis")]}
                                    fullWidth
                                />
                            </div>
                        }
                        {snapshotMicroAreaMetrics?.total_monitoring_counts.rating_count != 0 &&
                            <div className="noBreakComponent">
                                <div className="infoContentGraph">
                                    <h2>{t("reports.intervalDays", { count: snapshotMicroAreaMetrics?.daily_metrics.length })}</h2>
                                    <h2>{t("reports.ratingCollections", { count: snapshotMicroAreaMetrics?.total_monitoring_counts.rating_count })}</h2>
                                    <h2>{t("reports.averageOccupationDays", { value: Number(snapshotMicroAreaMetrics?.total_occupation_days.average_occupied_days).toFixed(1) })}</h2>
                                    <h2>{t("reports.averageRestDays", { value: Number(snapshotMicroAreaMetrics?.total_occupation_days.average_rest_days).toFixed(1) })}</h2>
                                </div>
                                {/* <ChartBarLine
                                    titleGraph={"Taxa de lotação instatânea e nota de pasto em manejo rotacionado"}
                                    dataColumn={lotInstNota?.data1 || []}
                                    dataLine={[]}
                                    colorIcons={["Média de Monitoramento"]}
                                    axisLabels={["", "Nota"]}
                                    fullWidth
                                    info={lotInstNotaMicroArea.map(inst => inst.info)}
                                /> */}
                                <ChartBar
                                    titleGraph={t("reports.rotationalRating")}
                                    data={lotInstNota?.data1}
                                    axisLabels={["", t("reports.averageRating")]}
                                    fullWidth
                                    info={lotInstNotaMicroArea?.map(inst => inst.info)}
                                    monoColor
                                />
                            </div>
                        }
                    </> : <></>
                    }
                    {typeManagement && typeManagement.map(((type: IOptionProps) => type.value)).includes("4") ? <>
                        {/* <ChartBarLine
                            titleGraph={"Taxa de lotação instatânea e número de mudanças de faixa diárias"}
                            dataColumn={lotInstMovFaixa?.data1 || []}
                            dataLine={lotInstMovFaixa?.data2 || []}
                            colorIcons={["Número de mudanças de faixa por dia", "Tx lotação instantânea"]}
                            axisLabels={["", "N° de faixa", "Tx lotação instantânea"]}
                            fullWidth
                            info={lotInstMovFaixaMicroArea.map(inst => inst.info)}
                        /> */}
                        <ChartBar
                            titleGraph={t("reports.dailyStripChanges")}
                            data={lotInstMovFaixa?.data1}
                            axisLabels={["", t("pastureMap.stripAxis")]}
                            fullWidth
                            info={lotInstMovFaixaMicroArea?.map(inst => inst.info)}
                            monoColor
                        />
                    </> : <></>
                    }
                </div>
            </form >
        </div >
    );
}
